<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Integration Guide</h5>
        <p class="page-sub">API reference and code examples for all Mapiibs modules</p>
      </div>
      <div class="export-btns">
        <button class="export-btn" @click="exportPdf" :disabled="!!exporting">
          <svg class="export-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V8l-4-6H6z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12 2v5a1 1 0 001 1h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8 13h4M8 11h2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          {{ exporting === 'pdf' ? 'Generating…' : 'Export PDF' }}
        </button>
        <button class="export-btn export-btn--word" @click="exportWord" :disabled="!!exporting">
          <svg class="export-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V8l-4-6H6z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12 2v5a1 1 0 001 1h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M7 11l1.5 4L10 12l1.5 3L13 11" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          {{ exporting === 'word' ? 'Generating…' : 'Export Word' }}
        </button>
      </div>
    </div>

    <!-- Quick nav -->
    <div class="section-tabs">
      <button v-for="s in sections" :key="s.id" class="sec-tab" :class="{ 'sec-tab--active': active === s.id }" @click="active = s.id">
        {{ s.label }}
      </button>
    </div>

    <!-- ── Authentication ─────────────────────────────────────────────── -->
    <div v-if="active === 'auth'" class="doc-section">
      <h3 class="doc-h3">Authentication</h3>
      <p class="doc-p">All API requests share the same base URL. Some endpoints are scoped to a specific company using a company code path segment.</p>

      <h4 class="doc-h4">Base URLs</h4>
      <div class="url-table">
        <div class="url-row">
          <span class="url-label">Base URL</span>
          <code class="url-value">{{ internalUrl }}/</code>
          <span class="url-desc">General endpoints (login, public data)</span>
        </div>
        <div class="url-row">
          <span class="url-label">With company code</span>
          <code class="url-value">{{ internalUrl }}/{{ companyCode }}/</code>
          <span class="url-desc">Company-scoped endpoints (all modules)</span>
        </div>
      </div>

      <h4 class="doc-h4">Option 1 — Bearer JWT token (all modules)</h4>
      <p class="doc-p">Obtain a token by calling <code>POST /login</code>, then pass it in every request.</p>
      <pre class="code-block">POST {{ internalUrl }}/login
Content-Type: application/json

{
  "email": "user@example.com",
  "password": "secret"
}

→ { "access_token": "eyJ...", "token_type": "bearer", "expires_in": 3600 }</pre>
      <pre class="code-block">Authorization: Bearer eyJ0eXAiOiJKV1Q...</pre>

      <h4 class="doc-h4">Option 2 — API key (all modules)</h4>
      <p class="doc-p">Generate a key from the <strong>API Keys</strong> page (requires the <code>manage_api_keys</code> permission). Pass it in the request header:</p>
      <pre class="code-block">Authorization: Bearer sk_live_YOUR_KEY
# or
X-Api-Key: sk_live_YOUR_KEY</pre>

      <h4 class="doc-h4">Error responses</h4>
      <div class="err-table">
        <div class="err-row"><span class="http-badge http-401">401</span><span>Missing or invalid token / API key</span></div>
        <div class="err-row"><span class="http-badge http-403">403</span><span>Authenticated but lacks the required permission</span></div>
        <div class="err-row"><span class="http-badge http-429">429</span><span>Rate limit exceeded (120 req / min per key)</span></div>
        <div class="err-row"><span class="http-badge http-422">422</span><span>Validation error — check the <code>message</code> field</span></div>
      </div>

      <h4 class="doc-h4">Response envelope</h4>
      <pre class="code-block">{
  "status": true,
  "message": "operation successful!",
  "data": { ... }
}</pre>
    </div>

    <!-- ── Visitors API ───────────────────────────────────────────────── -->
    <div v-if="active === 'visitors'" class="doc-section">
      <h3 class="doc-h3">Visitors API</h3>
      <p class="doc-p">
        <strong>Auth:</strong> API key &nbsp;·&nbsp;
        Scope required: <code class="scope-pill scope-read">visitors:read</code> for reads,
        <code class="scope-pill scope-write">visitors:write</code> for writes.
      </p>
      <div class="doc-note"><font-awesome-icon icon="info-circle" /> Base URL: <code>{{ baseUrl }}/visitors</code></div>

      <div v-for="ep in visitorEndpoints" :key="ep.method + ep.path" class="endpoint-card">
        <div class="ep-head">
          <span class="method-badge" :class="'method-' + ep.method.toLowerCase()">{{ ep.method }}</span>
          <code class="ep-path">{{ ep.path }}</code>
          <span class="ep-desc">{{ ep.desc }}</span>
        </div>
        <div v-if="ep.params" class="ep-params">
          <div v-for="p in ep.params" :key="p.name" class="ep-param">
            <code class="param-name">{{ p.name }}</code>
            <span class="param-type">{{ p.type }}</span>
            <span class="param-desc">{{ p.desc }}</span>
          </div>
        </div>
        <div v-if="ep.example" class="ep-example">
          <pre class="code-block code-block--sm">{{ ep.example }}</pre>
        </div>
      </div>
    </div>

    <!-- ── HR & People ────────────────────────────────────────────────── -->
    <div v-if="active === 'hr'" class="doc-section">
      <h3 class="doc-h3">HR &amp; People API</h3>
      <p class="doc-p">
        <strong>Auth:</strong> Bearer JWT &nbsp;·&nbsp;
        Permissions: <code class="scope-pill scope-read">hr_module</code>
        <code class="scope-pill scope-read">training_module</code>
        <code class="scope-pill scope-read">exits_module</code>
        <code class="scope-pill scope-read">view_pms_cycles</code>
      </p>
      <div class="doc-note"><font-awesome-icon icon="info-circle" /> Base paths: <code>{{ internalUrl }}/hr/</code> · <code>{{ internalUrl }}/payroll/</code> · <code>{{ internalUrl }}/training/</code> · <code>{{ internalUrl }}/exits/</code> · <code>{{ internalUrl }}/hr/pms/</code></div>

      <!-- Core HR -->
      <template v-for="grp in hrEndpointGroups" :key="grp.title">
        <h4 class="doc-h4">{{ grp.title }}</h4>
        <div v-for="ep in grp.endpoints" :key="ep.method + ep.path" class="endpoint-card">
          <div class="ep-head">
            <span class="method-badge" :class="'method-' + ep.method.toLowerCase()">{{ ep.method }}</span>
            <code class="ep-path">{{ ep.path }}</code>
            <span class="ep-desc">{{ ep.desc }}</span>
          </div>
          <div v-if="ep.params" class="ep-params">
            <div v-for="p in ep.params" :key="p.name" class="ep-param">
              <code class="param-name">{{ p.name }}</code>
              <span class="param-type">{{ p.type }}</span>
              <span class="param-desc">{{ p.desc }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Training -->
      <div class="module-divider">
        <span class="module-divider-label">Training &nbsp;·&nbsp; <code>{{ internalUrl }}/training/</code></span>
      </div>
      <template v-for="grp in trainingEndpointGroups" :key="'trn-' + grp.title">
        <h4 class="doc-h4">{{ grp.title }}</h4>
        <div v-for="ep in grp.endpoints" :key="ep.method + ep.path" class="endpoint-card">
          <div class="ep-head">
            <span class="method-badge" :class="'method-' + ep.method.toLowerCase()">{{ ep.method }}</span>
            <code class="ep-path">{{ ep.path }}</code>
            <span class="ep-desc">{{ ep.desc }}</span>
          </div>
          <div v-if="ep.params" class="ep-params">
            <div v-for="p in ep.params" :key="p.name" class="ep-param">
              <code class="param-name">{{ p.name }}</code>
              <span class="param-type">{{ p.type }}</span>
              <span class="param-desc">{{ p.desc }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Exit Management -->
      <div class="module-divider">
        <span class="module-divider-label">Exit Management &nbsp;·&nbsp; <code>{{ internalUrl }}/exits/</code></span>
      </div>
      <template v-for="grp in exitEndpointGroups" :key="'ext-' + grp.title">
        <h4 class="doc-h4">{{ grp.title }}</h4>
        <div v-for="ep in grp.endpoints" :key="ep.method + ep.path" class="endpoint-card">
          <div class="ep-head">
            <span class="method-badge" :class="'method-' + ep.method.toLowerCase()">{{ ep.method }}</span>
            <code class="ep-path">{{ ep.path }}</code>
            <span class="ep-desc">{{ ep.desc }}</span>
          </div>
          <div v-if="ep.params" class="ep-params">
            <div v-for="p in ep.params" :key="p.name" class="ep-param">
              <code class="param-name">{{ p.name }}</code>
              <span class="param-type">{{ p.type }}</span>
              <span class="param-desc">{{ p.desc }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Performance Management -->
      <div class="module-divider">
        <span class="module-divider-label">Performance Management (PMS) &nbsp;·&nbsp; <code>{{ internalUrl }}/hr/pms/</code></span>
      </div>
      <template v-for="grp in pmsEndpointGroups" :key="'pms-' + grp.title">
        <h4 class="doc-h4">{{ grp.title }}</h4>
        <div v-for="ep in grp.endpoints" :key="ep.method + ep.path" class="endpoint-card">
          <div class="ep-head">
            <span class="method-badge" :class="'method-' + ep.method.toLowerCase()">{{ ep.method }}</span>
            <code class="ep-path">{{ ep.path }}</code>
            <span class="ep-desc">{{ ep.desc }}</span>
          </div>
          <div v-if="ep.params" class="ep-params">
            <div v-for="p in ep.params" :key="p.name" class="ep-param">
              <code class="param-name">{{ p.name }}</code>
              <span class="param-type">{{ p.type }}</span>
              <span class="param-desc">{{ p.desc }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- ── Payroll ────────────────────────────────────────────────────── -->
    <div v-if="active === 'payroll'" class="doc-section">
      <h3 class="doc-h3">Payroll API</h3>
      <p class="doc-p">
        <strong>Auth:</strong> Bearer JWT &nbsp;·&nbsp;
        Permission: <code class="scope-pill scope-read">payroll_module</code>
      </p>
      <div class="doc-note"><font-awesome-icon icon="info-circle" /> Base path: <code>{{ internalUrl }}/payroll/</code></div>

      <template v-for="grp in payrollEndpointGroups" :key="grp.title">
        <h4 class="doc-h4">{{ grp.title }}</h4>
        <div v-for="ep in grp.endpoints" :key="ep.method + ep.path" class="endpoint-card">
          <div class="ep-head">
            <span class="method-badge" :class="'method-' + ep.method.toLowerCase()">{{ ep.method }}</span>
            <code class="ep-path">{{ ep.path }}</code>
            <span class="ep-desc">{{ ep.desc }}</span>
          </div>
          <div v-if="ep.params" class="ep-params">
            <div v-for="p in ep.params" :key="p.name" class="ep-param">
              <code class="param-name">{{ p.name }}</code>
              <span class="param-type">{{ p.type }}</span>
              <span class="param-desc">{{ p.desc }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- ── CRM ────────────────────────────────────────────────────────── -->
    <div v-if="active === 'crm'" class="doc-section">
      <h3 class="doc-h3">CRM API</h3>
      <p class="doc-p">
        <strong>Auth:</strong> Bearer JWT &nbsp;·&nbsp;
        Permission: <code class="scope-pill scope-read">crm_module</code>
      </p>
      <div class="doc-note"><font-awesome-icon icon="info-circle" /> Base path: <code>{{ internalUrl }}/crm/</code></div>

      <template v-for="grp in crmEndpointGroups" :key="grp.title">
        <h4 class="doc-h4">{{ grp.title }}</h4>
        <div v-for="ep in grp.endpoints" :key="ep.method + ep.path" class="endpoint-card">
          <div class="ep-head">
            <span class="method-badge" :class="'method-' + ep.method.toLowerCase()">{{ ep.method }}</span>
            <code class="ep-path">{{ ep.path }}</code>
            <span class="ep-desc">{{ ep.desc }}</span>
          </div>
          <div v-if="ep.params" class="ep-params">
            <div v-for="p in ep.params" :key="p.name" class="ep-param">
              <code class="param-name">{{ p.name }}</code>
              <span class="param-type">{{ p.type }}</span>
              <span class="param-desc">{{ p.desc }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- ── Inventory ──────────────────────────────────────────────────── -->
    <div v-if="active === 'inventory'" class="doc-section">
      <h3 class="doc-h3">Inventory API</h3>
      <p class="doc-p">
        <strong>Auth:</strong> Bearer JWT &nbsp;·&nbsp;
        Permission: <code class="scope-pill scope-read">inventory_mgt_module</code>
      </p>
      <div class="doc-note"><font-awesome-icon icon="info-circle" /> Base path: <code>{{ internalUrl }}/inventory/</code></div>

      <template v-for="grp in inventoryEndpointGroups" :key="grp.title">
        <h4 class="doc-h4">{{ grp.title }}</h4>
        <div v-for="ep in grp.endpoints" :key="ep.method + ep.path" class="endpoint-card">
          <div class="ep-head">
            <span class="method-badge" :class="'method-' + ep.method.toLowerCase()">{{ ep.method }}</span>
            <code class="ep-path">{{ ep.path }}</code>
            <span class="ep-desc">{{ ep.desc }}</span>
          </div>
          <div v-if="ep.params" class="ep-params">
            <div v-for="p in ep.params" :key="p.name" class="ep-param">
              <code class="param-name">{{ p.name }}</code>
              <span class="param-type">{{ p.type }}</span>
              <span class="param-desc">{{ p.desc }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- ── Finance ────────────────────────────────────────────────────── -->
    <div v-if="active === 'finance'" class="doc-section">
      <h3 class="doc-h3">Finance API</h3>
      <p class="doc-p">
        <strong>Auth:</strong> Bearer JWT &nbsp;·&nbsp;
        Permissions: <code class="scope-pill scope-read">accounting_module</code>
        <code class="scope-pill scope-read">expense_module</code>
        <code class="scope-pill scope-read">bank_module</code>
      </p>
      <div class="doc-note"><font-awesome-icon icon="info-circle" /> Base path: <code>{{ internalUrl }}/accounting/</code></div>

      <template v-for="grp in financeEndpointGroups" :key="grp.title">
        <h4 class="doc-h4">{{ grp.title }}</h4>
        <div v-for="ep in grp.endpoints" :key="ep.method + ep.path" class="endpoint-card">
          <div class="ep-head">
            <span class="method-badge" :class="'method-' + ep.method.toLowerCase()">{{ ep.method }}</span>
            <code class="ep-path">{{ ep.path }}</code>
            <span class="ep-desc">{{ ep.desc }}</span>
          </div>
          <div v-if="ep.params" class="ep-params">
            <div v-for="p in ep.params" :key="p.name" class="ep-param">
              <code class="param-name">{{ p.name }}</code>
              <span class="param-type">{{ p.type }}</span>
              <span class="param-desc">{{ p.desc }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- ── Procurement ────────────────────────────────────────────────── -->
    <div v-if="active === 'procurement'" class="doc-section">
      <h3 class="doc-h3">Procurement API</h3>
      <p class="doc-p">
        <strong>Auth:</strong> Bearer JWT &nbsp;·&nbsp;
        Permissions: <code class="scope-pill scope-read">view_vendors</code>
        <code class="scope-pill scope-read">view_purchase_requisitions</code>
        <code class="scope-pill scope-read">view_purchase_orders</code>
        <code class="scope-pill scope-read">view_sourcing_events</code>
      </p>
      <div class="doc-note"><font-awesome-icon icon="info-circle" /> Base path: <code>{{ internalUrl }}/procurement/</code></div>

      <template v-for="grp in procurementEndpointGroups" :key="grp.title">
        <h4 class="doc-h4">{{ grp.title }}</h4>
        <div v-for="ep in grp.endpoints" :key="ep.method + ep.path" class="endpoint-card">
          <div class="ep-head">
            <span class="method-badge" :class="'method-' + ep.method.toLowerCase()">{{ ep.method }}</span>
            <code class="ep-path">{{ ep.path }}</code>
            <span class="ep-desc">{{ ep.desc }}</span>
          </div>
          <div v-if="ep.params" class="ep-params">
            <div v-for="p in ep.params" :key="p.name" class="ep-param">
              <code class="param-name">{{ p.name }}</code>
              <span class="param-type">{{ p.type }}</span>
              <span class="param-desc">{{ p.desc }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- ── Projects ───────────────────────────────────────────────────── -->
    <div v-if="active === 'projects'" class="doc-section">
      <h3 class="doc-h3">Projects API</h3>
      <p class="doc-p">
        <strong>Auth:</strong> Bearer JWT &nbsp;·&nbsp;
        Permissions: <code class="scope-pill scope-read">view_projects</code>
        <code class="scope-pill scope-read">view_timesheets</code>
        <code class="scope-pill scope-read">view_project_budgets</code>
      </p>
      <div class="doc-note"><font-awesome-icon icon="info-circle" /> Base path: <code>{{ internalUrl }}/projects/</code></div>

      <template v-for="grp in projectEndpointGroups" :key="grp.title">
        <h4 class="doc-h4">{{ grp.title }}</h4>
        <div v-for="ep in grp.endpoints" :key="ep.method + ep.path" class="endpoint-card">
          <div class="ep-head">
            <span class="method-badge" :class="'method-' + ep.method.toLowerCase()">{{ ep.method }}</span>
            <code class="ep-path">{{ ep.path }}</code>
            <span class="ep-desc">{{ ep.desc }}</span>
          </div>
          <div v-if="ep.params" class="ep-params">
            <div v-for="p in ep.params" :key="p.name" class="ep-param">
              <code class="param-name">{{ p.name }}</code>
              <span class="param-type">{{ p.type }}</span>
              <span class="param-desc">{{ p.desc }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- ── Invoicing & Billing ───────────────────────────────────────── -->
    <div v-if="active === 'billing'" class="doc-section">
      <h3 class="doc-h3">Invoicing &amp; Billing API</h3>
      <p class="doc-p">
        <strong>Auth:</strong> Bearer JWT &nbsp;·&nbsp;
        Permissions: <code class="scope-pill scope-read">invoice_module</code>
        <code class="scope-pill scope-read">billing_module</code>
      </p>
      <div class="doc-note"><font-awesome-icon icon="info-circle" /> Base paths: <code>{{ internalUrl }}/invoice/</code> · <code>{{ internalUrl }}/billing/</code></div>

      <template v-for="grp in invoiceEndpointGroups" :key="grp.title">
        <h4 class="doc-h4">{{ grp.title }}</h4>
        <div v-for="ep in grp.endpoints" :key="ep.method + ep.path" class="endpoint-card">
          <div class="ep-head">
            <span class="method-badge" :class="'method-' + ep.method.toLowerCase()">{{ ep.method }}</span>
            <code class="ep-path">{{ ep.path }}</code>
            <span class="ep-desc">{{ ep.desc }}</span>
          </div>
          <div v-if="ep.params" class="ep-params">
            <div v-for="p in ep.params" :key="p.name" class="ep-param">
              <code class="param-name">{{ p.name }}</code>
              <span class="param-type">{{ p.type }}</span>
              <span class="param-desc">{{ p.desc }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- ── Webhooks ───────────────────────────────────────────────────── -->
    <div v-if="active === 'webhooks'" class="doc-section">
      <h3 class="doc-h3">Webhooks</h3>
      <p class="doc-p">Mapiibs sends a signed HTTP POST to your endpoint whenever a subscribed event occurs.</p>

      <h4 class="doc-h4">Request headers sent with every delivery</h4>
      <pre class="code-block">X-Mapiibs-Event:   visitor.checked_in
X-Mapiibs-Sign:    sha256=abc123...  (HMAC-SHA256 of the raw body)
X-Mapiibs-Attempt: 1                (increments on retries)</pre>

      <h4 class="doc-h4">Verify the signature</h4>
      <pre class="code-block">// Node.js
const crypto = require('crypto')
const rawBody = req.body
const sig     = req.headers['x-mapiibs-sign']
const expected = 'sha256=' + crypto
  .createHmac('sha256', WEBHOOK_SECRET)
  .update(rawBody)
  .digest('hex')
if (!crypto.timingSafeEqual(Buffer.from(sig), Buffer.from(expected))) {
  return res.status(401).json({ error: 'Invalid signature' })
}</pre>

      <pre class="code-block">// Python (Flask)
import hmac, hashlib
sig      = request.headers.get('X-Mapiibs-Sign', '')
expected = 'sha256=' + hmac.new(
    WEBHOOK_SECRET.encode(), request.data, hashlib.sha256
).hexdigest()
if not hmac.compare_digest(sig, expected):
    abort(401)</pre>

      <h4 class="doc-h4">Payload structure</h4>
      <pre class="code-block">{
  "event":       "visitor.checked_in",
  "occurred_at": "2026-10-22T09:14:00+01:00",
  "data": {
    // event-specific fields — see supported events below
  }
}</pre>

      <h4 class="doc-h4">Retry policy</h4>
      <p class="doc-p">Deliveries returning a non-2xx status are retried 3 times with exponential back-off: <strong>10 s → 60 s → 5 min</strong>. After 10 consecutive failures the webhook is automatically disabled.</p>

      <h4 class="doc-h4">Supported events</h4>
      <div v-for="(evs, group) in eventGroups" :key="group" class="ev-group-doc">
        <div class="ev-group-doc-title">{{ group }}</div>
        <div v-for="e in evs" :key="e" class="ev-row"><code>{{ e }}</code></div>
      </div>
    </div>

    <!-- ── Code Examples ──────────────────────────────────────────────── -->
    <div v-if="active === 'sdk'" class="doc-section">
      <h3 class="doc-h3">Code Examples</h3>

      <h4 class="doc-h4">JavaScript / Node.js — Visitors (API key)</h4>
      <pre class="code-block">const API_KEY = 'sk_live_your_key'
const BASE    = '{{ baseUrl }}'

async function getActiveVisitors() {
  const res = await fetch(`${BASE}/visitors/active`, {
    headers: { 'Authorization': `Bearer ${API_KEY}` }
  })
  return (await res.json()).data
}

async function checkIn(visitId, location) {
  const res = await fetch(`${BASE}/visitors/visits/${visitId}/check-in`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${API_KEY}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ location })
  })
  return res.json()
}</pre>

      <h4 class="doc-h4">JavaScript / Node.js — Internal API (JWT)</h4>
      <pre class="code-block">const BASE = '{{ internalUrl }}'
let token  = null

async function login(email, password) {
  const res = await fetch(`${BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  })
  token = (await res.json()).access_token
}

async function getLeads(params = {}) {
  const qs  = new URLSearchParams(params).toString()
  const res = await fetch(`${BASE}/crm/leads?${qs}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  })
  return (await res.json()).data
}

async function createEmployee(payload) {
  const res = await fetch(`${BASE}/payroll/employees`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  })
  return res.json()
}</pre>

      <h4 class="doc-h4">Python — Visitors (API key)</h4>
      <pre class="code-block">import requests

API_KEY = 'sk_live_your_key'
BASE    = '{{ baseUrl }}'
HEADERS = {'Authorization': f'Bearer {API_KEY}'}

def get_active_visitors():
    r = requests.get(f'{BASE}/visitors/active', headers=HEADERS)
    r.raise_for_status()
    return r.json()['data']

def check_out(visit_id):
    r = requests.post(f'{BASE}/visitors/visits/{visit_id}/check-out', headers=HEADERS)
    r.raise_for_status()
    return r.json()['data']</pre>

      <h4 class="doc-h4">Python — Internal API (JWT)</h4>
      <pre class="code-block">import requests

BASE    = '{{ internalUrl }}'
session = requests.Session()

def login(email, password):
    r = session.post(f'{BASE}/login', json={'email': email, 'password': password})
    r.raise_for_status()
    token = r.json()['access_token']
    session.headers.update({'Authorization': f'Bearer {token}'})

def get_leads(status=None):
    params = {'status': status} if status else {}
    r = session.get(f'{BASE}/crm/leads', params=params)
    r.raise_for_status()
    return r.json()['data']

def run_payroll(payload):
    r = session.post(f'{BASE}/payroll', json=payload)
    r.raise_for_status()
    return r.json()['data']</pre>

      <h4 class="doc-h4">PHP — Internal API (JWT)</h4>
      <pre class="code-block">$base  = '{{ internalUrl }}';
$token = null;

function login($email, $password) {
    global $base, $token;
    $ch = curl_init("$base/login");
    curl_setopt_array($ch, [
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_POST           => true,
        CURLOPT_POSTFIELDS     => json_encode(['email'=>$email,'password'=>$password]),
        CURLOPT_HTTPHEADER     => ['Content-Type: application/json'],
    ]);
    $token = json_decode(curl_exec($ch), true)['access_token'];
}

function apiGet($path) {
    global $base, $token;
    $ch = curl_init("$base$path");
    curl_setopt_array($ch, [
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_HTTPHEADER     => ["Authorization: Bearer $token"],
    ]);
    return json_decode(curl_exec($ch), true)['data'];
}</pre>
    </div>

  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'DeveloperDocs',
  data() {
    return {
      active: 'auth',
      exporting: null,
      eventGroups: {},
      sections: [
        { id: 'auth',        label: 'Authentication'      },
        { id: 'visitors',    label: 'Visitors'            },
        { id: 'hr',          label: 'HR & People'         },
        { id: 'payroll',     label: 'Payroll'             },
        { id: 'crm',         label: 'CRM'                 },
        { id: 'inventory',   label: 'Inventory'           },
        { id: 'finance',     label: 'Finance'             },
        { id: 'billing',     label: 'Invoicing & Billing' },
        { id: 'procurement', label: 'Procurement'         },
        { id: 'projects',    label: 'Projects'            },
        { id: 'webhooks',    label: 'Webhooks'            },
        { id: 'sdk',         label: 'Code Examples'       },
      ],

      // ── Visitors v1 ────────────────────────────────────────────────────
      visitorEndpoints: [
        { method: 'GET',  path: '/visits',              desc: 'Paginated visit history',
          params: [
            { name: 'status',   type: 'string',  desc: 'pre_registered | checked_in | checked_out' },
            { name: 'from',     type: 'date',    desc: 'Start date YYYY-MM-DD' },
            { name: 'to',       type: 'date',    desc: 'End date YYYY-MM-DD'  },
            { name: 'per_page', type: 'integer', desc: 'Results per page (default 20)' },
          ]},
        { method: 'GET',  path: '/visits/{id}',         desc: 'Get a single visit by ID' },
        { method: 'GET',  path: '/active',              desc: 'List all currently checked-in visitors' },
        { method: 'GET',  path: '/visitor-types',       desc: 'List configured visitor types' },
        { method: 'GET',  path: '/sites',               desc: 'List configured sites and zones' },
        { method: 'POST', path: '/visits',              desc: 'Pre-register a visitor',
          params: [
            { name: 'first_name',       type: 'string*',  desc: 'Visitor first name'     },
            { name: 'last_name',        type: 'string*',  desc: 'Visitor last name'       },
            { name: 'email',            type: 'string',   desc: 'Visitor email'           },
            { name: 'purpose',          type: 'string*',  desc: 'Reason for visit'        },
            { name: 'scheduled_at',     type: 'datetime*',desc: 'Planned arrival time'    },
            { name: 'host_employee_id', type: 'uuid',     desc: 'ID of the host employee' },
          ]},
        { method: 'POST', path: '/visits/{id}/check-in',  desc: 'Check in a pre-registered visitor',
          params: [{ name: 'location', type: 'string', desc: 'Meeting room or area' }]},
        { method: 'POST', path: '/visits/{id}/check-out', desc: 'Check out an active visitor' },
      ],

      // ── HR & People ─────────────────────────────────────────────────────
      hrEndpointGroups: [
        {
          title: 'Employees',
          endpoints: [
            { method: 'GET',    path: '/payroll/employees',                desc: 'List all employees (paginated)' },
            { method: 'GET',    path: '/payroll/employees/{id}',           desc: 'Get a single employee record' },
            { method: 'POST',   path: '/payroll/employees',                desc: 'Create a new employee',
              params: [
                { name: 'first_name',    type: 'string*', desc: 'First name'       },
                { name: 'last_name',     type: 'string*', desc: 'Last name'        },
                { name: 'email',         type: 'string*', desc: 'Work email'       },
                { name: 'department_id', type: 'uuid',    desc: 'Department UUID'  },
                { name: 'position_id',   type: 'uuid',    desc: 'Position UUID'    },
                { name: 'hire_date',     type: 'date',    desc: 'Date of hire'     },
              ]},
            { method: 'PUT',    path: '/payroll/employees/{id}',           desc: 'Update employee personal details' },
            { method: 'PUT',    path: '/payroll/employees/{id}/employment', desc: 'Update employment info (position, department, status)' },
            { method: 'PUT',    path: '/payroll/employees/{id}/financial',  desc: 'Update bank/financial details' },
            { method: 'DELETE', path: '/payroll/employees/{id}',           desc: 'Archive / delete an employee record' },
          ],
        },
        {
          title: 'Shifts',
          endpoints: [
            { method: 'GET',    path: '/hr/shift',                desc: 'List shift templates (paginated)' },
            { method: 'GET',    path: '/hr/shift/{id}',           desc: 'Get a single shift template' },
            { method: 'POST',   path: '/hr/shift',                desc: 'Create a shift template' },
            { method: 'POST',   path: '/hr/shift/assign',         desc: 'Assign a shift to an employee',
              params: [
                { name: 'employee_id', type: 'uuid*',  desc: 'Employee to assign'        },
                { name: 'shift_id',    type: 'uuid*',  desc: 'Shift template ID'         },
                { name: 'date',        type: 'date*',  desc: 'Date of the assignment'    },
              ]},
            { method: 'DELETE', path: '/hr/shift/assignment/{id}',desc: 'Remove a shift assignment' },
            { method: 'GET',    path: '/hr/shift/employee/{id}',  desc: 'Get all shifts for a specific employee' },
          ],
        },
        {
          title: 'Rotas',
          endpoints: [
            { method: 'GET',  path: '/hr/rota',                     desc: 'List rotas' },
            { method: 'GET',  path: '/hr/rota/{id}',                desc: 'Get a rota with all entries' },
            { method: 'POST', path: '/hr/rota',                     desc: 'Create a new rota' },
            { method: 'PUT',  path: '/hr/rota/{id}/publish',        desc: 'Publish a rota (notify employees)' },
            { method: 'POST', path: '/hr/rota/{id}/roll-forward',   desc: 'Roll a published rota forward by one period' },
            { method: 'POST', path: '/hr/rota/{id}/entries',        desc: 'Add a single entry to a rota' },
            { method: 'POST', path: '/hr/rota/{id}/entries/bulk',   desc: 'Bulk-add entries to a rota' },
            { method: 'GET',  path: '/hr/rota/{id}/leave-conflicts',desc: 'Detect leave conflicts in a rota' },
          ],
        },
        {
          title: 'Attendance',
          endpoints: [
            { method: 'GET',  path: '/hr/attendance',           desc: 'List attendance records (paginated)' },
            { method: 'GET',  path: '/hr/attendance/today-status', desc: 'Today\'s clock-in status for current user' },
            { method: 'GET',  path: '/hr/attendance/summary',   desc: 'Attendance summary report' },
            { method: 'POST', path: '/hr/attendance/clock-in',  desc: 'Clock the current user in' },
            { method: 'POST', path: '/hr/attendance/clock-out', desc: 'Clock the current user out' },
            { method: 'PUT',  path: '/hr/attendance/{id}/edit', desc: 'Manually edit an attendance record' },
          ],
        },
        {
          title: 'Leave',
          endpoints: [
            { method: 'GET',  path: '/hr/leave',             desc: 'List leave requests (paginated)' },
            { method: 'GET',  path: '/hr/leave/{id}',        desc: 'Get a leave request' },
            { method: 'GET',  path: '/hr/leave/balances',    desc: 'Get leave balances for current user' },
            { method: 'POST', path: '/hr/leave',             desc: 'Submit a leave request',
              params: [
                { name: 'leave_type_id', type: 'uuid*', desc: 'Leave type UUID'       },
                { name: 'start_date',    type: 'date*', desc: 'First day of leave'    },
                { name: 'end_date',      type: 'date*', desc: 'Last day of leave'     },
                { name: 'reason',        type: 'string',desc: 'Optional reason'       },
              ]},
            { method: 'PUT',  path: '/hr/leave/{id}/approve',desc: 'Approve a leave request' },
            { method: 'PUT',  path: '/hr/leave/{id}/reject', desc: 'Reject a leave request' },
            { method: 'PUT',  path: '/hr/leave/{id}/cancel', desc: 'Cancel a leave request' },
            { method: 'GET',  path: '/hr/leave-type',        desc: 'List leave types' },
          ],
        },
        {
          title: 'Announcements',
          endpoints: [
            { method: 'GET',    path: '/hr/announcement',              desc: 'List company announcements' },
            { method: 'POST',   path: '/hr/announcement',              desc: 'Post a new announcement' },
            { method: 'PUT',    path: '/hr/announcement/{id}/status',  desc: 'Publish or unpublish an announcement' },
            { method: 'POST',   path: '/hr/announcement/{id}/like',    desc: 'Toggle like on an announcement' },
            { method: 'GET',    path: '/hr/announcement/{id}/comments',desc: 'List comments on an announcement' },
            { method: 'POST',   path: '/hr/announcement/{id}/comments',desc: 'Post a comment' },
          ],
        },
        {
          title: 'Loans',
          endpoints: [
            { method: 'GET',  path: '/hr/loan-type',          desc: 'List loan types' },
            { method: 'GET',  path: '/hr/loan',               desc: 'List employee loans' },
            { method: 'POST', path: '/hr/loan',               desc: 'Create a loan request' },
            { method: 'PUT',  path: '/hr/loan/{id}/approve',  desc: 'Approve a loan request' },
            { method: 'GET',  path: '/hr/loan/{id}/repayments',desc: 'List repayment schedule for a loan' },
          ],
        },
        {
          title: 'Departments, Positions & Locations',
          endpoints: [
            { method: 'GET',  path: '/payroll/department/active', desc: 'List active departments' },
            { method: 'GET',  path: '/hr/department',            desc: 'List all departments' },
            { method: 'POST', path: '/hr/department',            desc: 'Create a department' },
            { method: 'GET',  path: '/payroll/position/active',  desc: 'List active positions' },
            { method: 'GET',  path: '/hr/position',              desc: 'List all positions' },
            { method: 'POST', path: '/hr/position',              desc: 'Create a position' },
            { method: 'GET',  path: '/hr/location',              desc: 'List business locations' },
            { method: 'POST', path: '/hr/location',              desc: 'Create a location' },
            { method: 'GET',  path: '/hr/public-holiday',        desc: 'List public holidays' },
          ],
        },
      ],

      // ── Payroll ─────────────────────────────────────────────────────────
      payrollEndpointGroups: [
        {
          title: 'Payroll Runs',
          endpoints: [
            { method: 'POST', path: '/payroll',           desc: 'Calculate and save a payroll run',
              params: [
                { name: 'period_start',  type: 'date*',  desc: 'Payroll period start'        },
                { name: 'period_end',    type: 'date*',  desc: 'Payroll period end'           },
                { name: 'employee_ids',  type: 'array',  desc: 'Subset of employees (blank = all)' },
              ]},
            { method: 'GET',  path: '/payroll',           desc: 'List historical payroll runs' },
            { method: 'GET',  path: '/payroll/{id}',      desc: 'Get a single payroll run with line items' },
          ],
        },
        {
          title: 'Pay Grades',
          endpoints: [
            { method: 'GET',    path: '/hr/pay-grade',         desc: 'List pay grades' },
            { method: 'POST',   path: '/hr/pay-grade',         desc: 'Create a pay grade' },
            { method: 'PUT',    path: '/hr/pay-grade/{id}',    desc: 'Update a pay grade' },
            { method: 'DELETE', path: '/hr/pay-grade/{id}',    desc: 'Delete a pay grade' },
          ],
        },
        {
          title: 'Salary Components',
          endpoints: [
            { method: 'GET',    path: '/hr/salary-component',       desc: 'List salary components (allowances, deductions)' },
            { method: 'POST',   path: '/hr/salary-component',       desc: 'Create a salary component' },
            { method: 'PUT',    path: '/hr/salary-component/{id}',  desc: 'Update a salary component' },
            { method: 'DELETE', path: '/hr/salary-component/{id}',  desc: 'Delete a salary component' },
          ],
        },
        {
          title: 'Financial Periods',
          endpoints: [
            { method: 'GET',  path: '/hr/payroll-lock',         desc: 'List payroll lock periods' },
            { method: 'POST', path: '/hr/payroll-lock',         desc: 'Create a payroll lock period' },
            { method: 'PUT',  path: '/hr/payroll-lock/{id}/override', desc: 'Override a lock period (admin only)' },
          ],
        },
      ],

      // ── CRM ─────────────────────────────────────────────────────────────
      crmEndpointGroups: [
        {
          title: 'Leads',
          endpoints: [
            { method: 'GET',    path: '/crm/leads',             desc: 'List leads (paginated, filterable)' },
            { method: 'GET',    path: '/crm/leads/{id}',        desc: 'Get a single lead' },
            { method: 'POST',   path: '/crm/leads',             desc: 'Create a new lead',
              params: [
                { name: 'first_name',   type: 'string*', desc: 'Lead first name'  },
                { name: 'last_name',    type: 'string*', desc: 'Lead last name'   },
                { name: 'email',        type: 'string',  desc: 'Lead email'       },
                { name: 'phone',        type: 'string',  desc: 'Lead phone'       },
                { name: 'source',       type: 'string',  desc: 'Lead source'      },
                { name: 'assigned_to',  type: 'uuid',    desc: 'Assigned employee ID' },
              ]},
            { method: 'PUT',    path: '/crm/leads/{id}',        desc: 'Update a lead' },
            { method: 'DELETE', path: '/crm/leads/{id}',        desc: 'Delete a lead' },
            { method: 'POST',   path: '/crm/leads/{id}/convert',desc: 'Convert lead to contact + account + opportunity' },
            { method: 'GET',    path: '/crm/leads/export',      desc: 'Export leads to CSV/Excel' },
            { method: 'POST',   path: '/crm/leads/import',      desc: 'Bulk-import leads from CSV' },
          ],
        },
        {
          title: 'Contacts',
          endpoints: [
            { method: 'GET',    path: '/crm/contacts',       desc: 'List contacts (paginated)' },
            { method: 'GET',    path: '/crm/contacts/{id}',  desc: 'Get a single contact' },
            { method: 'POST',   path: '/crm/contacts',       desc: 'Create a contact' },
            { method: 'PUT',    path: '/crm/contacts/{id}',  desc: 'Update a contact' },
            { method: 'DELETE', path: '/crm/contacts/{id}',  desc: 'Delete a contact' },
          ],
        },
        {
          title: 'Accounts',
          endpoints: [
            { method: 'GET', path: '/crm/accounts',          desc: 'List accounts (companies / organisations)' },
            { method: 'GET', path: '/crm/accounts/{id}',     desc: 'Get a single account' },
            { method: 'GET', path: '/crm/accounts/{id}/360', desc: '360° view: contacts, opps, activities, invoices' },
            { method: 'PUT', path: '/crm/accounts/{id}',     desc: 'Update an account' },
          ],
        },
        {
          title: 'Opportunities',
          endpoints: [
            { method: 'GET',  path: '/crm/opportunities',          desc: 'List opportunities (paginated)' },
            { method: 'GET',  path: '/crm/opportunities/pipeline', desc: 'Kanban pipeline view grouped by stage' },
            { method: 'GET',  path: '/crm/opportunities/{id}',     desc: 'Get a single opportunity' },
            { method: 'POST', path: '/crm/opportunities',          desc: 'Create an opportunity' },
            { method: 'PUT',  path: '/crm/opportunities/{id}',     desc: 'Update an opportunity (stage, value, close date)' },
            { method: 'PUT',  path: '/crm/opportunities/{id}/lines', desc: 'Sync line items on an opportunity' },
          ],
        },
        {
          title: 'Activities',
          endpoints: [
            { method: 'GET',  path: '/crm/activities',                          desc: 'List all activities' },
            { method: 'GET',  path: '/crm/activities/{subjectType}/{subjectId}', desc: 'Activities for a specific lead, contact, or account' },
            { method: 'POST', path: '/crm/activities',                          desc: 'Log a call, email, meeting, or task' },
            { method: 'PUT',  path: '/crm/activities/{id}',                     desc: 'Update an activity' },
            { method: 'POST', path: '/crm/activities/{id}/notes',               desc: 'Append a note to an activity' },
          ],
        },
        {
          title: 'Segments',
          endpoints: [
            { method: 'GET',  path: '/crm/segments',              desc: 'List contact segments' },
            { method: 'POST', path: '/crm/segments',              desc: 'Create a segment (static or dynamic rules)' },
            { method: 'POST', path: '/crm/segments/{id}/evaluate',desc: 'Re-evaluate dynamic segment membership' },
            { method: 'POST', path: '/crm/segments/{id}/members', desc: 'Manually add contacts to a static segment' },
          ],
        },
        {
          title: 'Campaigns',
          endpoints: [
            { method: 'GET',  path: '/crm/campaigns',             desc: 'List campaigns' },
            { method: 'POST', path: '/crm/campaigns',             desc: 'Create a campaign' },
            { method: 'PUT',  path: '/crm/campaigns/{id}',        desc: 'Update a campaign' },
            { method: 'POST', path: '/crm/campaigns/{id}/send',   desc: 'Schedule or immediately send a campaign' },
          ],
        },
      ],

      // ── Inventory ────────────────────────────────────────────────────────
      inventoryEndpointGroups: [
        {
          title: 'Products',
          endpoints: [
            { method: 'GET',    path: '/inventory/product',           desc: 'List all products' },
            { method: 'GET',    path: '/inventory/product/active/all',desc: 'List active products only' },
            { method: 'GET',    path: '/inventory/product/{id}',      desc: 'Get a single product' },
            { method: 'POST',   path: '/inventory/product',           desc: 'Create a product',
              params: [
                { name: 'name',        type: 'string*', desc: 'Product name'                 },
                { name: 'sku',         type: 'string',  desc: 'Stock keeping unit'           },
                { name: 'category_id', type: 'uuid',    desc: 'Category UUID'                },
                { name: 'unit_id',     type: 'uuid',    desc: 'Unit of measurement UUID'     },
                { name: 'cost_price',  type: 'number',  desc: 'Cost price'                   },
                { name: 'sell_price',  type: 'number',  desc: 'Selling price'                },
              ]},
            { method: 'PUT',    path: '/inventory/product/{id}',      desc: 'Update a product' },
            { method: 'DELETE', path: '/inventory/product/{id}',      desc: 'Delete a product' },
          ],
        },
        {
          title: 'Stock Adjustments',
          endpoints: [
            { method: 'GET',  path: '/inventory/stock-adjustment',        desc: 'List stock adjustments' },
            { method: 'GET',  path: '/inventory/stock-adjustment/{id}',   desc: 'Get a single adjustment' },
            { method: 'POST', path: '/inventory/stock-adjustment',        desc: 'Create a stock adjustment (in / out / recount)' },
            { method: 'POST', path: '/inventory/stock-adjustment/{id}/reverse', desc: 'Reverse a stock adjustment' },
          ],
        },
        {
          title: 'Stock Transfers',
          endpoints: [
            { method: 'GET',  path: '/inventory/stock-transfer',       desc: 'List stock transfers between locations' },
            { method: 'POST', path: '/inventory/stock-transfer',       desc: 'Initiate a stock transfer' },
            { method: 'POST', path: '/inventory/stock-transfer/{id}/reverse', desc: 'Reverse a transfer' },
          ],
        },
        {
          title: 'Internal Issues',
          endpoints: [
            { method: 'GET',  path: '/inventory/internal-issue',              desc: 'List internal stock issues' },
            { method: 'POST', path: '/inventory/internal-issue',              desc: 'Raise an internal issue' },
            { method: 'POST', path: '/inventory/internal-issue/{id}/approve', desc: 'Approve an internal issue' },
            { method: 'POST', path: '/inventory/internal-issue/{id}/cancel',  desc: 'Cancel an internal issue' },
          ],
        },
        {
          title: 'Categories & Units',
          endpoints: [
            { method: 'GET',  path: '/inventory/category',              desc: 'List product categories' },
            { method: 'POST', path: '/inventory/category',              desc: 'Create a category' },
            { method: 'GET',  path: '/inventory/product/uom/unit-of-measurement', desc: 'List units of measurement' },
          ],
        },
      ],

      // ── Invoicing & Billing ──────────────────────────────────────────────
      invoiceEndpointGroups: [
        {
          title: 'Invoices',
          endpoints: [
            { method: 'GET',  path: '/invoice/all',                       desc: 'List invoices (paginated)' },
            { method: 'GET',  path: '/invoice/detail/{id}',               desc: 'Get a single invoice with line items' },
            { method: 'POST', path: '/invoice/create',                    desc: 'Create an invoice',
              params: [
                { name: 'customer_id',  type: 'uuid*',  desc: 'Customer UUID'               },
                { name: 'issue_date',   type: 'date*',  desc: 'Invoice date'                },
                { name: 'due_date',     type: 'date*',  desc: 'Due date'                    },
                { name: 'items',        type: 'array*', desc: 'Line items (description, qty, unit_price)' },
              ]},
            { method: 'PUT',  path: '/invoice/update',                    desc: 'Update a draft invoice' },
            { method: 'POST', path: '/invoice/record-payment',            desc: 'Record a payment against an invoice',
              params: [
                { name: 'invoice_id',   type: 'uuid*',   desc: 'Invoice UUID'               },
                { name: 'amount',       type: 'number*', desc: 'Amount paid'                },
                { name: 'payment_date', type: 'date*',   desc: 'Date of payment'            },
                { name: 'method',       type: 'string',  desc: 'cash | bank_transfer | card' },
              ]},
            { method: 'GET',  path: '/invoice/payments',                  desc: 'List all invoice payments' },
            { method: 'GET',  path: '/invoice/send-reminder/{id}',        desc: 'Send payment reminder to the customer' },
            { method: 'POST', path: '/invoice/send-bulk-reminder',        desc: 'Send reminders for all overdue invoices' },
            { method: 'DELETE',path: '/invoice/delete/{id}',              desc: 'Delete an invoice' },
          ],
        },
        {
          title: 'Recurring Invoices',
          endpoints: [
            { method: 'PUT',  path: '/invoice/cancel-recurring/{id}',     desc: 'Cancel a recurring invoice schedule' },
          ],
        },
        {
          title: 'Billing Groups',
          endpoints: [
            { method: 'GET',    path: '/billing/all',                     desc: 'List billing subscriptions' },
            { method: 'GET',    path: '/billing/detail/{id}',             desc: 'Get a single billing record' },
            { method: 'GET',    path: '/billing/invoices/{id}',           desc: 'List invoices for a billing subscription' },
            { method: 'GET',    path: '/billing/group',                   desc: 'List billing groups' },
            { method: 'POST',   path: '/billing/group',                   desc: 'Create a billing group' },
            { method: 'PUT',    path: '/billing/group/{id}',              desc: 'Update a billing group' },
            { method: 'DELETE', path: '/billing/group/{id}',              desc: 'Delete a billing group' },
            { method: 'POST',   path: '/billing/group/{id}/customers',    desc: 'Add customers to a billing group' },
          ],
        },
      ],

      // ── Finance ──────────────────────────────────────────────────────────
      financeEndpointGroups: [
        {
          title: 'Ledgers',
          endpoints: [
            { method: 'GET',    path: '/accounting/ledgers',              desc: 'List all ledger accounts' },
            { method: 'GET',    path: '/accounting/active-ledgers',       desc: 'List active ledger accounts only' },
            { method: 'POST',   path: '/accounting/ledger/create',        desc: 'Create a ledger account' },
            { method: 'PUT',    path: '/accounting/ledger/update',        desc: 'Update a ledger account' },
            { method: 'DELETE', path: '/accounting/ledger/{id}',          desc: 'Delete a ledger account' },
            { method: 'POST',   path: '/accounting/ledger/import',        desc: 'Bulk-import ledgers from CSV' },
          ],
        },
        {
          title: 'Journals & Transactions',
          endpoints: [
            { method: 'GET',  path: '/accounting/transactions',           desc: 'List general ledger transactions' },
            { method: 'GET',  path: '/accounting/journal',                desc: 'List journal entries' },
            { method: 'POST', path: '/accounting/journal',                desc: 'Create a journal entry',
              params: [
                { name: 'date',        type: 'date*',   desc: 'Transaction date'         },
                { name: 'narration',   type: 'string*', desc: 'Memo / description'       },
                { name: 'lines',       type: 'array*',  desc: 'Debit/credit line items'  },
              ]},
            { method: 'PUT',  path: '/accounting/journal/{id}',           desc: 'Update a draft journal' },
            { method: 'PUT',  path: '/accounting/journal/approve/{id}',   desc: 'Approve and post a journal' },
            { method: 'DELETE',path: '/accounting/journal/{id}',          desc: 'Delete a draft journal' },
          ],
        },
        {
          title: 'Expenses',
          endpoints: [
            { method: 'GET',    path: '/accounting/expense',              desc: 'List expense claims' },
            { method: 'POST',   path: '/accounting/expense',              desc: 'Submit an expense claim' },
            { method: 'PUT',    path: '/accounting/expense/{id}',         desc: 'Update an expense claim' },
            { method: 'PUT',    path: '/accounting/expense/{id}/approve', desc: 'Approve an expense claim' },
            { method: 'DELETE', path: '/accounting/expense/{id}',         desc: 'Delete an expense claim' },
            { method: 'GET',    path: '/accounting/expense/category',     desc: 'List expense categories' },
          ],
        },
        {
          title: 'Bank Accounts',
          endpoints: [
            { method: 'GET',  path: '/accounting/bank',          desc: 'List bank accounts' },
            { method: 'POST', path: '/accounting/bank',          desc: 'Add a bank account' },
            { method: 'PUT',  path: '/accounting/bank/{id}',     desc: 'Update a bank account' },
            { method: 'DELETE',path: '/accounting/bank/{id}',    desc: 'Remove a bank account' },
          ],
        },
      ],

      // ── Procurement ──────────────────────────────────────────────────────
      procurementEndpointGroups: [
        {
          title: 'Vendors',
          endpoints: [
            { method: 'GET',    path: '/procurement/vendors',       desc: 'List vendors (paginated)' },
            { method: 'GET',    path: '/procurement/vendors/{id}',  desc: 'Get a vendor profile' },
            { method: 'POST',   path: '/procurement/vendors',       desc: 'Register a new vendor' },
            { method: 'PUT',    path: '/procurement/vendors/{id}',  desc: 'Update a vendor' },
            { method: 'DELETE', path: '/procurement/vendors/{id}',  desc: 'Archive a vendor' },
          ],
        },
        {
          title: 'Purchase Requisitions',
          endpoints: [
            { method: 'GET',  path: '/procurement/purchase-requisitions',           desc: 'List purchase requisitions' },
            { method: 'GET',  path: '/procurement/purchase-requisitions/{id}',      desc: 'Get a single PR' },
            { method: 'POST', path: '/procurement/purchase-requisitions',           desc: 'Create a purchase requisition' },
            { method: 'PUT',  path: '/procurement/purchase-requisitions/{id}',      desc: 'Update a PR' },
            { method: 'PUT',  path: '/procurement/purchase-requisitions/{id}/approve', desc: 'Approve a PR' },
            { method: 'DELETE',path: '/procurement/purchase-requisitions/{id}',     desc: 'Delete a PR' },
          ],
        },
        {
          title: 'Purchase Orders',
          endpoints: [
            { method: 'GET',  path: '/procurement/purchase-orders',          desc: 'List purchase orders' },
            { method: 'GET',  path: '/procurement/purchase-orders/{id}',     desc: 'Get a single PO' },
            { method: 'POST', path: '/procurement/purchase-orders',          desc: 'Create a purchase order' },
            { method: 'PUT',  path: '/procurement/purchase-orders/{id}',     desc: 'Update a PO' },
            { method: 'POST', path: '/procurement/purchase-orders/{id}/supply-items', desc: 'Record item delivery against a PO' },
            { method: 'POST', path: '/procurement/payments/{id}/reverse',    desc: 'Reverse a PO payment' },
          ],
        },
        {
          title: 'Sourcing Events',
          endpoints: [
            { method: 'GET',  path: '/procurement/sourcing-events',                   desc: 'List sourcing events (RFQ / RFP / tender)' },
            { method: 'GET',  path: '/procurement/sourcing-events/{id}',              desc: 'Get a sourcing event with bids' },
            { method: 'POST', path: '/procurement/sourcing-events',                   desc: 'Create a sourcing event' },
            { method: 'PUT',  path: '/procurement/sourcing-events/{id}',              desc: 'Update a sourcing event' },
            { method: 'POST', path: '/procurement/sourcing-events/{id}/evaluate',     desc: 'Score and rank vendor bids' },
          ],
        },
      ],

      // ── Projects ─────────────────────────────────────────────────────────
      projectEndpointGroups: [
        {
          title: 'Projects',
          endpoints: [
            { method: 'GET',    path: '/projects',                desc: 'List projects (paginated)' },
            { method: 'GET',    path: '/projects/{id}',           desc: 'Get a project with team and status' },
            { method: 'POST',   path: '/projects',                desc: 'Create a project',
              params: [
                { name: 'name',        type: 'string*', desc: 'Project name'               },
                { name: 'client_id',   type: 'uuid',    desc: 'Client UUID'                },
                { name: 'start_date',  type: 'date*',   desc: 'Project start date'         },
                { name: 'end_date',    type: 'date',    desc: 'Planned end date'           },
                { name: 'budget',      type: 'number',  desc: 'Total budget amount'        },
              ]},
            { method: 'PUT',    path: '/projects/{id}',           desc: 'Update a project' },
            { method: 'POST',   path: '/projects/{id}/activate',  desc: 'Move project to active status' },
            { method: 'POST',   path: '/projects/{id}/close',     desc: 'Close a project' },
            { method: 'DELETE', path: '/projects/{id}',           desc: 'Delete a project' },
          ],
        },
        {
          title: 'Work Breakdown Structure',
          endpoints: [
            { method: 'GET',  path: '/projects/{id}/wbs',          desc: 'Get WBS tree for a project' },
            { method: 'POST', path: '/projects/{id}/wbs',          desc: 'Create a WBS node (phase or deliverable)' },
            { method: 'PUT',  path: '/projects/{id}/wbs/{nodeId}', desc: 'Update a WBS node' },
            { method: 'POST', path: '/projects/{id}/wbs/reorder',  desc: 'Reorder WBS nodes' },
          ],
        },
        {
          title: 'Timesheets',
          endpoints: [
            { method: 'GET',  path: '/projects/timesheets',             desc: 'List timesheet entries (paginated)' },
            { method: 'POST', path: '/projects/timesheets',             desc: 'Log a timesheet entry',
              params: [
                { name: 'project_id',  type: 'uuid*',   desc: 'Project UUID'             },
                { name: 'date',        type: 'date*',   desc: 'Work date'                },
                { name: 'hours',       type: 'number*', desc: 'Hours worked'             },
                { name: 'description', type: 'string',  desc: 'Work description'         },
              ]},
            { method: 'PUT',  path: '/projects/timesheets/{id}/approve', desc: 'Approve a timesheet entry' },
          ],
        },
        {
          title: 'Budgets',
          endpoints: [
            { method: 'GET',  path: '/projects/{id}/budgets',       desc: 'Get budget vs actual for a project' },
            { method: 'POST', path: '/projects/{id}/budgets',       desc: 'Set or update a project budget line' },
            { method: 'POST', path: '/projects/budgets/{id}/approve', desc: 'Approve a budget line' },
          ],
        },
        {
          title: 'Team',
          endpoints: [
            { method: 'GET',  path: '/projects/{id}/team',          desc: 'List project team members' },
            { method: 'POST', path: '/projects/{id}/team',          desc: 'Add a team member' },
            { method: 'DELETE',path: '/projects/{id}/team/{memberId}', desc: 'Remove a team member' },
          ],
        },
      ],

      // ── Training ─────────────────────────────────────────────────────────
      trainingEndpointGroups: [
        {
          title: 'Courses',
          endpoints: [
            { method: 'GET',    path: '/training/courses',                           desc: 'List courses (paginated)' },
            { method: 'GET',    path: '/training/courses/{id}',                      desc: 'Get course with sections and lessons' },
            { method: 'POST',   path: '/training/courses',                           desc: 'Create a course',
              params: [
                { name: 'title',       type: 'string*', desc: 'Course title'              },
                { name: 'category_id', type: 'uuid',    desc: 'Category UUID'             },
                { name: 'duration_min',type: 'integer', desc: 'Estimated duration (minutes)' },
                { name: 'audience',    type: 'string',  desc: 'all | department | position' },
              ]},
            { method: 'PUT',    path: '/training/courses/{id}',                      desc: 'Update a course' },
            { method: 'POST',   path: '/training/courses/{id}/publish',              desc: 'Publish a course (make available to learners)' },
            { method: 'POST',   path: '/training/courses/{id}/archive',              desc: 'Archive a course' },
            { method: 'DELETE', path: '/training/courses/{id}',                      desc: 'Delete a course (only drafts)' },
          ],
        },
        {
          title: 'Sections & Lessons',
          endpoints: [
            { method: 'POST',   path: '/training/courses/{id}/sections',                          desc: 'Add a section to a course' },
            { method: 'PUT',    path: '/training/courses/{id}/sections/{sectionId}',              desc: 'Update a section' },
            { method: 'DELETE', path: '/training/courses/{id}/sections/{sectionId}',              desc: 'Delete a section' },
            { method: 'POST',   path: '/training/courses/{id}/lessons',                           desc: 'Add a lesson (video, document, quiz)' },
            { method: 'PUT',    path: '/training/courses/{id}/lessons/{lessonId}',                desc: 'Update a lesson' },
            { method: 'DELETE', path: '/training/courses/{id}/lessons/{lessonId}',               desc: 'Delete a lesson' },
          ],
        },
        {
          title: 'Enrolments',
          endpoints: [
            { method: 'GET',  path: '/training/enrolments',              desc: 'List enrolments (paginated)' },
            { method: 'POST', path: '/training/enrolments',              desc: 'Enrol one or more employees on a course' },
            { method: 'PUT',  path: '/training/enrolments/{id}/complete',desc: 'Manually mark an enrolment complete' },
            { method: 'DELETE',path: '/training/enrolments/{id}',        desc: 'Revoke an enrolment' },
          ],
        },
        {
          title: 'Certificates & Reports',
          endpoints: [
            { method: 'GET', path: '/training/certificates',           desc: 'List issued certificates' },
            { method: 'GET', path: '/training/certificates/{id}',      desc: 'Download a certificate PDF' },
            { method: 'GET', path: '/training/dashboard',              desc: 'Training completion stats dashboard' },
          ],
        },
      ],

      // ── Exit Management ──────────────────────────────────────────────────
      exitEndpointGroups: [
        {
          title: 'Exit Cases',
          endpoints: [
            { method: 'GET',  path: '/exits',                       desc: 'List exit cases (paginated, filterable by stage)' },
            { method: 'GET',  path: '/exits/pipeline',              desc: 'Kanban pipeline view grouped by stage' },
            { method: 'GET',  path: '/exits/{id}',                  desc: 'Get an exit case with clearance tasks' },
            { method: 'POST', path: '/exits',                       desc: 'Open an exit case for an employee',
              params: [
                { name: 'employee_id',  type: 'uuid*',  desc: 'Departing employee UUID'   },
                { name: 'exit_type',    type: 'string*',desc: 'resignation | termination | retirement' },
                { name: 'exit_date',    type: 'date*',  desc: 'Last working day'           },
                { name: 'reason',       type: 'string', desc: 'Exit reason / notes'        },
              ]},
            { method: 'PUT',  path: '/exits/{id}',                  desc: 'Update an exit case' },
            { method: 'POST', path: '/exits/{id}/transition',       desc: 'Advance the exit case to the next stage',
              params: [
                { name: 'stage', type: 'string*', desc: 'Target stage slug' },
                { name: 'notes', type: 'string',  desc: 'Transition notes' },
              ]},
          ],
        },
        {
          title: 'Clearance Tasks',
          endpoints: [
            { method: 'GET',  path: '/exits/{id}/clearance',              desc: 'List clearance tasks for an exit case' },
            { method: 'POST', path: '/exits/{id}/clearance',              desc: 'Add a clearance task' },
            { method: 'PUT',  path: '/exits/{id}/clearance/{taskId}',     desc: 'Update a clearance task (mark done, add notes)' },
            { method: 'DELETE',path: '/exits/{id}/clearance/{taskId}',    desc: 'Remove a clearance task' },
          ],
        },
        {
          title: 'Self-Service (Employee)',
          endpoints: [
            { method: 'POST', path: '/exits/ess/resign',           desc: 'Employee submits their own resignation' },
            { method: 'GET',  path: '/exits/ess',                  desc: 'Get the current employee\'s exit case' },
            { method: 'POST', path: '/exits/ess/{id}/withdraw',    desc: 'Withdraw a resignation (before approval)' },
          ],
        },
        {
          title: 'Settings',
          endpoints: [
            { method: 'GET', path: '/exits/settings',  desc: 'Get exit module settings (stages, clearance template)' },
            { method: 'PUT', path: '/exits/settings',  desc: 'Update exit settings' },
          ],
        },
      ],

      // ── PMS ──────────────────────────────────────────────────────────────
      pmsEndpointGroups: [
        {
          title: 'Cycles',
          endpoints: [
            { method: 'GET',    path: '/hr/pms/cycles',                   desc: 'List performance cycles' },
            { method: 'GET',    path: '/hr/pms/cycles/{id}',              desc: 'Get a cycle with participants' },
            { method: 'POST',   path: '/hr/pms/cycles',                   desc: 'Create a performance cycle',
              params: [
                { name: 'name',       type: 'string*', desc: 'Cycle name'                    },
                { name: 'start_date', type: 'date*',   desc: 'Cycle start date'              },
                { name: 'end_date',   type: 'date*',   desc: 'Cycle end date'                },
                { name: 'type',       type: 'string*', desc: 'annual | semi-annual | quarterly' },
              ]},
            { method: 'PUT',    path: '/hr/pms/cycles/{id}/status',       desc: 'Change cycle status (draft → active → closed)' },
            { method: 'POST',   path: '/hr/pms/cycles/{id}/participants', desc: 'Enrol employees into a cycle' },
            { method: 'DELETE', path: '/hr/pms/cycles/{id}',              desc: 'Delete a cycle' },
          ],
        },
        {
          title: 'KPIs',
          endpoints: [
            { method: 'GET',    path: '/hr/pms/kpis',         desc: 'List KPIs (key performance indicators)' },
            { method: 'POST',   path: '/hr/pms/kpis',         desc: 'Create a KPI' },
            { method: 'PUT',    path: '/hr/pms/kpis/{id}',    desc: 'Update a KPI' },
            { method: 'POST',   path: '/hr/pms/kpis/{id}/assign', desc: 'Assign a KPI to employees or departments' },
            { method: 'DELETE', path: '/hr/pms/kpis/{id}',    desc: 'Delete a KPI' },
          ],
        },
        {
          title: 'Competencies',
          endpoints: [
            { method: 'GET',    path: '/hr/pms/competencies',          desc: 'List competency frameworks' },
            { method: 'POST',   path: '/hr/pms/competencies',          desc: 'Create a competency' },
            { method: 'PUT',    path: '/hr/pms/competencies/{id}',     desc: 'Update a competency' },
            { method: 'POST',   path: '/hr/pms/competencies/{id}/assign', desc: 'Assign competency to roles / departments' },
          ],
        },
        {
          title: 'Goals',
          endpoints: [
            { method: 'GET',  path: '/hr/pms/goals',              desc: 'List goals (all employees, manager view)' },
            { method: 'GET',  path: '/hr/pms/goals/my',           desc: 'List goals for the current employee' },
            { method: 'POST', path: '/hr/pms/goals',              desc: 'Create a goal' },
            { method: 'PUT',  path: '/hr/pms/goals/{id}',         desc: 'Update a goal' },
            { method: 'PUT',  path: '/hr/pms/goals/{id}/status',  desc: 'Update goal progress status' },
            { method: 'POST', path: '/hr/pms/goals/{id}/approve', desc: 'Approve / reject a goal' },
            { method: 'POST', path: '/hr/pms/goals/{id}/comment', desc: 'Add a comment to a goal' },
          ],
        },
        {
          title: 'Reviews',
          endpoints: [
            { method: 'GET',  path: '/hr/pms/reviews',             desc: 'List performance reviews' },
            { method: 'GET',  path: '/hr/pms/reviews/{id}',        desc: 'Get a review with scores' },
            { method: 'POST', path: '/hr/pms/reviews',             desc: 'Create a review' },
            { method: 'PUT',  path: '/hr/pms/reviews/{id}',        desc: 'Submit review scores' },
            { method: 'POST', path: '/hr/pms/reviews/{id}/approve', desc: 'Approve a completed review' },
          ],
        },
        {
          title: 'Reports & Bonus',
          endpoints: [
            { method: 'GET',  path: '/hr/pms/reports',        desc: 'Aggregated performance report across all employees' },
            { method: 'GET',  path: '/hr/pms/bonus',          desc: 'List bonus calculations' },
            { method: 'POST', path: '/hr/pms/bonus/sync-payroll', desc: 'Push approved bonuses to payroll' },
          ],
        },
      ],
    }
  },
  computed: {
    companyCode() {
      return this.$route?.params?.code ?? window.location.pathname.split('/')[1] ?? 'COMPANY_CODE'
    },
    baseUrl() {
      return `${window.location.origin}/api/v1/${this.companyCode}`
    },
    internalUrl() {
      return `${window.location.origin}/api`
    },
  },
  async created() {
    try {
      const r = await axios.get('developer/webhooks/meta/events')
      this.eventGroups = r.data.data?.grouped ?? {}
    } catch { /* non-critical */ }
  },
  methods: {
    // ── Export PDF ───────────────────────────────────────────────────────────
    exportPdf() {
      if (this.exporting) return
      this.exporting = 'pdf'
      import('jspdf').then(({ default: jsPDF }) => {
        import('jspdf-autotable').then(() => {
          try {
            const doc    = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
            const margin = 14
            const pageW  = doc.internal.pageSize.getWidth()
            const cW     = pageW - margin * 2

            const MC = {
              GET:    { fill: [219,234,254], text: [29,78,216]   },
              POST:   { fill: [220,252,231], text: [22,101,52]   },
              PUT:    { fill: [254,243,199], text: [146,64,14]   },
              PATCH:  { fill: [255,237,213], text: [154,52,18]   },
              DELETE: { fill: [254,226,226], text: [220,38,38]   },
            }

            // Cover
            doc.setFontSize(22); doc.setFont('helvetica','bold'); doc.setTextColor(26,43,69)
            doc.text('Mapiibs Integration Guide', margin, 24)
            doc.setFontSize(10); doc.setFont('helvetica','normal'); doc.setTextColor(122,144,176)
            doc.text('Full API reference — all modules', margin, 32)
            doc.text(`Base URL: ${this.baseUrl}`, margin, 38)
            doc.text(`Generated: ${new Date().toLocaleDateString('en-GB',{year:'numeric',month:'long',day:'numeric'})}`, margin, 44)

            let y = 54
            const sections = this._collectSections()

            sections.forEach((section, si) => {
              if (si > 0) { doc.addPage(); y = margin }

              doc.setFontSize(14); doc.setFont('helvetica','bold'); doc.setTextColor(26,43,69)
              doc.text(section.title, margin, y); y += 5

              if (section.note) {
                doc.setFontSize(9); doc.setFont('helvetica','normal'); doc.setTextColor(30,64,175)
                const nl = doc.splitTextToSize(section.note, cW)
                doc.text(nl, margin, y); y += nl.length * 4 + 3
              }

              section.groups.forEach(grp => {
                if (grp.title) {
                  doc.setFontSize(11); doc.setFont('helvetica','bold'); doc.setTextColor(26,43,69)
                  doc.text(grp.title, margin, y + 4); y += 8
                }
                if (!grp.endpoints?.length) return

                // body: each row is [method, path, {desc, params}] — we store structured data
                // so didParseCell can size the row and didDrawCell can style it properly.
                const body = []
                grp.endpoints.forEach(ep => {
                  if (!ep.method) return
                  body.push([ep.method, ep.path, { desc: ep.desc, params: ep.params || [] }])
                })
                if (!body.length) return

                // Measure how tall a description cell needs to be so autoTable allocates
                // enough row height before we do the custom draw.
                const descColW = cW - 96
                const padH = 6  // top + bottom padding
                const lineH = 4.2
                const cellContent = (row) => {
                  const { desc, params } = row[2]
                  if (!params.length) return doc.splitTextToSize(desc, descColW)
                  return [
                    ...doc.splitTextToSize(desc, descColW),
                    '',
                    ...params.flatMap(p => doc.splitTextToSize(`  ${p.name} (${p.type})  ${p.desc}`, descColW)),
                  ]
                }

                doc.autoTable({
                  startY: y,
                  margin: { left: margin, right: margin },
                  head: [['Method', 'Endpoint / Path', 'Description / Parameters']],
                  body: body.map(r => [r[0], r[1], '']),  // pass empty string; we draw manually
                  theme: 'grid',
                  styles: { fontSize: 9, cellPadding: 3, textColor: [26,43,69], lineColor: [228,234,242], lineWidth: 0.2, valign: 'top' },
                  headStyles: { fillColor: [248,250,252], textColor: [122,144,176], fontStyle: 'bold', fontSize: 8, cellPadding: 2 },
                  columnStyles: {
                    0: { cellWidth: 18 },
                    1: { cellWidth: 78, font: 'courier', fontSize: 8 },
                    2: { cellWidth: descColW },
                  },
                  didParseCell(data) {
                    if (data.section !== 'body') return
                    const row = body[data.row.index]
                    if (!row) return
                    if (data.column.index === 0) {
                      const c = MC[row[0]]
                      if (c) {
                        data.cell.styles.fillColor = c.fill
                        data.cell.styles.textColor = c.text
                        data.cell.styles.fontStyle  = 'bold'
                        data.cell.styles.halign     = 'center'
                        data.cell.styles.fontSize   = 7.5
                      }
                    }
                    if (data.column.index === 2) {
                      // Force row height to fit all param lines
                      const lines = cellContent(row)
                      data.cell.styles.minCellHeight = lines.length * lineH + padH
                    }
                  },
                  didDrawCell(data) {
                    if (data.section !== 'body' || data.column.index !== 2) return
                    const row = body[data.row.index]
                    if (!row) return
                    const { desc, params } = row[2]
                    const padL = data.cell.padding('left')
                    const maxW = data.cell.width - padL - data.cell.padding('right')
                    const x  = data.cell.x + padL
                    let cy = data.cell.y + data.cell.padding('top') + 3

                    // Description line
                    doc.setFont('helvetica', 'normal')
                    doc.setFontSize(9)
                    doc.setTextColor(26, 43, 69)
                    const descWrapped = doc.splitTextToSize(desc, maxW)
                    doc.text(descWrapped, x, cy)
                    cy += descWrapped.length * lineH

                    // Param lines
                    if (params.length) {
                      cy += 1.5
                      doc.setFontSize(7.8)
                      params.forEach(p => {
                        const line = `  ${p.name}  (${p.type})   ${p.desc}`
                        const wrapped = doc.splitTextToSize(line, maxW)
                        // Name part bold-ish
                        doc.setTextColor(60, 80, 120)
                        doc.text(wrapped, x, cy)
                        cy += wrapped.length * lineH
                      })
                    }
                  },
                })
                y = doc.lastAutoTable.finalY + 7
              })
            })

            doc.save('mapiibs-integration-guide.pdf')
          } finally {
            this.exporting = null
          }
        })
      })
    },

    // ── Export Word ──────────────────────────────────────────────────────────
    async exportWord() {
      if (this.exporting) return
      this.exporting = 'word'
      try {
        const {
          Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
          HeadingLevel, WidthType, BorderStyle,
        } = await import('docx')

        const MC = {
          GET: 'DBEAFE', POST: 'DCFCE7', PUT: 'FEF3C7',
          PATCH: 'FFEDD5', DELETE: 'FEE2E2',
        }

        const border = { style: BorderStyle.SINGLE, size: 1, color: 'E4EAF2' }
        const tableBorders = { top: border, bottom: border, left: border, right: border, insideH: border, insideV: border }

        const mkCell = (paragraphs, opts = {}) => new TableCell({
          children: Array.isArray(paragraphs) ? paragraphs : [new Paragraph({ children: paragraphs })],
          borders: tableBorders,
          shading: opts.fill ? { fill: opts.fill, type: 'clear' } : undefined,
          width: opts.width,
        })

        const makeHeaderRow = () => new TableRow({
          tableHeader: true,
          children: [
            mkCell([new Paragraph({ children: [new TextRun({ text: 'Method',           bold: true, size: 16, color: '7a90b0' })] })], { fill: 'F8FAFC', width: { size: 10, type: WidthType.PERCENTAGE } }),
            mkCell([new Paragraph({ children: [new TextRun({ text: 'Endpoint / Path', bold: true, size: 16, color: '7a90b0' })] })], { fill: 'F8FAFC', width: { size: 42, type: WidthType.PERCENTAGE } }),
            mkCell([new Paragraph({ children: [new TextRun({ text: 'Description',      bold: true, size: 16, color: '7a90b0' })] })], { fill: 'F8FAFC', width: { size: 48, type: WidthType.PERCENTAGE } }),
          ],
        })

        const mkEndpointRows = (endpoints) => endpoints.filter(ep => ep.method).map(ep => new TableRow({
          children: [
            mkCell([new Paragraph({ children: [new TextRun({ text: ep.method, bold: true, size: 16 })] })],
              { fill: MC[ep.method] || 'FFFFFF', width: { size: 10, type: WidthType.PERCENTAGE } }),
            mkCell([new Paragraph({ children: [new TextRun({ text: ep.path, font: 'Courier New', size: 16 })] })],
              { width: { size: 42, type: WidthType.PERCENTAGE } }),
            mkCell([
              new Paragraph({ children: [new TextRun({ text: ep.desc, size: 18 })] }),
              ...(ep.params || []).map(p => new Paragraph({
                children: [
                  new TextRun({ text: p.name + ' ', bold: true, size: 15, color: '1a2b45' }),
                  new TextRun({ text: '(' + p.type + ') ', italics: true, size: 14, color: '7a90b0' }),
                  new TextRun({ text: p.desc, size: 14, color: '4a5e78' }),
                ],
                indent: { left: 360 },
              })),
            ], { width: { size: 48, type: WidthType.PERCENTAGE } }),
          ],
        }))

        const children = []

        // Cover
        children.push(new Paragraph({ children: [new TextRun({ text: 'Mapiibs Integration Guide', bold: true, size: 52, color: '1a2b45' })], spacing: { after: 160 } }))
        children.push(new Paragraph({ children: [new TextRun({ text: 'Full API reference — all modules', size: 22, color: '7a90b0' })], spacing: { after: 80 } }))
        children.push(new Paragraph({ children: [new TextRun({ text: `Base URL: ${this.baseUrl}`, size: 20, color: '4a5e78' })], spacing: { after: 80 } }))
        children.push(new Paragraph({ children: [new TextRun({ text: `Generated: ${new Date().toLocaleDateString('en-GB',{year:'numeric',month:'long',day:'numeric'})}`, size: 18, color: '7a90b0' })], spacing: { after: 400 } }))

        const sections = this._collectSections()

        sections.forEach((section, si) => {
          children.push(new Paragraph({ text: section.title, heading: HeadingLevel.HEADING_1, pageBreakBefore: si > 0 }))

          if (section.note) {
            children.push(new Paragraph({ children: [new TextRun({ text: section.note, italics: true, size: 18, color: '1e40af' })], spacing: { after: 100 } }))
          }

          section.groups.forEach(grp => {
            if (grp.title) {
              children.push(new Paragraph({ text: grp.title, heading: HeadingLevel.HEADING_2 }))
            }
            if (!grp.endpoints?.length) return

            const rows = mkEndpointRows(grp.endpoints)
            if (!rows.length) return

            children.push(new Table({
              width: { size: 100, type: WidthType.PERCENTAGE },
              rows: [makeHeaderRow(), ...rows],
              borders: tableBorders,
            }))
            children.push(new Paragraph({ text: '', spacing: { after: 160 } }))
          })
        })

        const wordDoc = new Document({ sections: [{ children }] })
        const blob = await Packer.toBlob(wordDoc)
        this._downloadBlob(blob, 'mapiibs-integration-guide.docx')
      } finally {
        this.exporting = null
      }
    },

    _downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob)
      const a   = document.createElement('a')
      a.href     = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    },

    _collectSections() {
      const prefix = (label, groups) => groups.map(g => ({ ...g, title: `${label} — ${g.title}` }))
      return [
        {
          title: 'Authentication',
          note: `Base URL: ${this.internalUrl}/  ·  Company-scoped: ${this.internalUrl}/${this.companyCode}/`,
          groups: [{ title: null, endpoints: [
            { method: 'POST', path: '/login',  desc: 'Obtain a Bearer JWT token', params: [
              { name: 'email',    type: 'string*', desc: 'User email address' },
              { name: 'password', type: 'string*', desc: 'Password' },
            ]},
            { method: 'GET',  path: '/logout', desc: 'Invalidate the current JWT token' },
          ]}],
        },
        {
          title: 'Visitors API',
          note: `Scope: visitors:read (GET) / visitors:write (POST, PUT, DELETE)  ·  Base: ${this.baseUrl}/visitors`,
          groups: [{ title: null, endpoints: this.visitorEndpoints }],
        },
        {
          title: 'HR & People API',
          note: 'Base paths: /hr/  /payroll/  /training/  /exits/  /hr/pms/',
          groups: [
            ...this.hrEndpointGroups,
            ...prefix('Training', this.trainingEndpointGroups),
            ...prefix('Exit Management', this.exitEndpointGroups),
            ...prefix('Performance Management', this.pmsEndpointGroups),
          ],
        },
        { title: 'Payroll API',           note: 'Base path: /payroll/',       groups: this.payrollEndpointGroups     },
        { title: 'CRM API',               note: 'Base path: /crm/',            groups: this.crmEndpointGroups         },
        { title: 'Inventory API',         note: 'Base path: /inventory/',      groups: this.inventoryEndpointGroups   },
        { title: 'Finance API',           note: 'Base path: /accounting/',     groups: this.financeEndpointGroups     },
        { title: 'Invoicing & Billing',   note: 'Base paths: /invoice/  /billing/', groups: this.invoiceEndpointGroups },
        { title: 'Procurement API',       note: 'Base path: /procurement/',    groups: this.procurementEndpointGroups },
        { title: 'Projects API',          note: 'Base path: /projects/',       groups: this.projectEndpointGroups     },
      ]
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.export-btns { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
.export-btn  { display:inline-flex; align-items:center; gap:7px; padding:8px 15px; border-radius:8px; border:1.5px solid #e4eaf2; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; cursor:pointer; font-family:inherit; transition:background .15s, border-color .15s, color .15s; white-space:nowrap; }
.export-btn:hover:not(:disabled) { background:#eff6ff; border-color:#93c5fd; color:#1d4ed8; }
.export-btn:disabled { opacity:.55; cursor:not-allowed; }
.export-btn--word:hover:not(:disabled) { background:#f0fdf4; border-color:#86efac; color:#166534; }
.export-icon { width:15px; height:15px; flex-shrink:0; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.section-tabs { display:flex; gap:4px; flex-wrap:wrap; background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:4px; margin-bottom:24px; }
.sec-tab { background:none; border:none; cursor:pointer; font-size:13px; font-weight:600; color:#7a90b0; padding:7px 16px; border-radius:7px; font-family:inherit; transition:background .1s, color .1s; }
.sec-tab:hover  { background:#eff6ff; color:#004AAD; }
.sec-tab--active { background:#004AAD; color:#fff; }

.doc-section { max-width:780px; }
.doc-h3 { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 10px; }
.doc-h4 { font-size:14px; font-weight:700; color:#1a2b45; margin:20px 0 8px; }
.doc-p  { font-size:13px; color:#4a5e78; line-height:1.6; margin:0 0 14px; }
.doc-note { background:#eff6ff; border-radius:8px; padding:10px 14px; font-size:13px; color:#1e40af; margin-bottom:16px; }
.doc-note code { background:rgba(255,255,255,.6); padding:1px 6px; border-radius:4px; }

code { font-family:monospace; font-size:12px; background:#f0f4f8; color:#1a2b45; padding:1px 5px; border-radius:4px; }
.code-block { background:#1a2b45; color:#e2e8f0; border-radius:10px; padding:14px 18px; font-size:12px; font-family:monospace; white-space:pre-wrap; word-break:break-all; overflow-x:auto; margin:0 0 16px; }
.code-block--sm { font-size:11px; padding:10px 14px; }

.url-table { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; overflow:hidden; margin-bottom:20px; }
.url-row   { display:flex; align-items:baseline; gap:14px; padding:11px 16px; border-bottom:1px solid #f0f4f8; flex-wrap:wrap; }
.url-row:last-child { border-bottom:none; }
.url-label { font-size:12px; font-weight:700; color:#7a90b0; min-width:130px; flex-shrink:0; }
.url-value { font-size:12px; color:#1a2b45; word-break:break-all; }
.url-desc  { font-size:12px; color:#7a90b0; }

.err-table { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; overflow:hidden; margin-bottom:14px; }
.err-row   { display:flex; align-items:center; gap:12px; padding:10px 14px; border-bottom:1px solid #f0f4f8; font-size:13px; color:#4a5e78; }
.err-row:last-child { border-bottom:none; }
.http-badge { display:inline-block; padding:2px 8px; border-radius:6px; font-size:12px; font-weight:700; font-family:monospace; min-width:36px; text-align:center; }
.http-401 { background:#fee2e2; color:#dc2626; }
.http-403 { background:#fef3c7; color:#d97706; }
.http-429 { background:#fff7ed; color:#c2410c; }
.http-422 { background:#eff6ff; color:#1d4ed8; }

.endpoint-card { border:1px solid #e4eaf2; border-radius:10px; padding:12px 16px; margin-bottom:8px; }
.ep-head   { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.ep-path   { font-size:13px; color:#1a2b45; }
.ep-desc   { font-size:12px; color:#7a90b0; }
.ep-params { margin-top:10px; padding-top:10px; border-top:1px solid #f0f4f8; display:flex; flex-direction:column; gap:5px; }
.ep-param  { display:flex; align-items:center; gap:8px; font-size:12px; }
.param-name { min-width:150px; }
.param-type { color:#7a90b0; min-width:70px; font-family:monospace; font-size:11px; }
.param-desc { color:#4a5e78; }
.ep-example { margin-top:10px; }

.method-badge { display:inline-block; padding:3px 8px; border-radius:5px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; min-width:52px; text-align:center; }
.method-get    { background:#dbeafe; color:#1d4ed8; }
.method-post   { background:#dcfce7; color:#166534; }
.method-put    { background:#fef3c7; color:#92400e; }
.method-delete { background:#fee2e2; color:#dc2626; }

.scope-pill { display:inline-block; padding:2px 8px; border-radius:20px; font-size:11px; font-weight:600; margin-right:3px; }
.scope-read  { background:#f0fdf4; color:#16a34a; }
.scope-write { background:#fff7ed; color:#c2410c; }

.ev-group-doc { margin-bottom:14px; }
.ev-group-doc-title { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; margin-bottom:8px; }
.ev-row { padding:5px 0; font-size:13px; }

.module-divider { display:flex; align-items:center; gap:12px; margin:28px 0 4px; }
.module-divider::before,.module-divider::after { content:''; flex:1; height:1px; background:#e4eaf2; }
.module-divider-label { font-size:12px; font-weight:700; color:#7a90b0; white-space:nowrap; }
.module-divider-label code { background:#eff6ff; color:#1e40af; }
</style>
